<template>
  <section class="employees-section">
    <h2 class="employees-section-title">{{ title }}</h2>
    <p v-if="permissions" class="employees-section-permissions">
      <span class="employees-section-permissions-label">Permissions:</span>
      {{ permissions }}
    </p>
    <p v-if="rows.length === 0" class="employees-section-empty">
      {{ emptyText }}
    </p>
    <ul v-else class="employees-list">
      <li
        v-for="row in rows"
        :key="row.email"
        class="employees-row"
      >
        <div class="employees-row-info">
          <div class="employees-row-name">{{ row.name || "(no name)" }}</div>
          <div class="employees-row-email">{{ row.email }}</div>
        </div>
        <div v-if="canEdit" class="employees-row-actions">
          <button
            type="button"
            class="employees-action-btn"
            :aria-label="`Edit ${row.email}`"
            @click="onEditClick(row, $event)"
          >
            <span class="mdi mdi-pencil-outline" aria-hidden="true"></span>
            Edit
          </button>
          <button
            type="button"
            class="employees-action-btn employees-action-btn--danger"
            :disabled="!canDeleteRow(row)"
            :aria-label="`Remove ${row.email}`"
            @click="onDeleteClick(row)"
          >
            <span class="mdi mdi-trash-can-outline" aria-hidden="true"></span>
            Remove
          </button>
        </div>
        <span
          v-else-if="lockedHint"
          class="employees-row-locked-hint"
        >
          {{ lockedHint }}
        </span>
      </li>
    </ul>
  </section>
</template>

<script>
// Pure presentational section card for the team page. Props in, events out.
// Renders one role band (Owner / Administrator / Employee) of the team list
// and emits `edit` / `delete` so the parent page owns dialog state.
export default {
  name: "EmployeeSection",
  props: {
    title: { type: String, required: true },
    emptyText: { type: String, default: "" },
    rows: { type: Array, required: true },
    canEdit: { type: Boolean, default: false },
    canDeleteRow: { type: Function, default: () => false },
    // When canEdit is false, render this hint next to each row so users
    // understand the row isn't broken - it's intentionally read-only.
    lockedHint: { type: String, default: "" },
    permissions: { type: String, default: "" },
  },
  emits: ["edit", "delete"],
  setup(props, { emit }) {
    const onEditClick = (row, event) => {
      emit("edit", row, event.currentTarget);
    };
    const onDeleteClick = (row) => {
      if (props.canDeleteRow(row)) emit("delete", row);
    };
    return {
      onEditClick,
      onDeleteClick,
    };
  },
};
</script>

<style scoped>
.employees-section {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 18px 20px;
}

.employees-section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  margin: 0 0 12px;
  text-transform: none;
  letter-spacing: 0.04em;
}

.employees-section-empty {
  margin: 0;
  font-size: 13px;
  color: var(--lulu-text-muted);
  font-style: italic;
}

.employees-section-permissions {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--lulu-text-muted);
}

.employees-section-permissions-label {
  font-weight: 700;
  color: var(--lulu-text-strong);
}

.employees-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.employees-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 6px 12px 14px;
  background: var(--lulu-surface-soft);
  border-radius: 12px;
  border: 1px solid var(--lulu-border);
}

.employees-row-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.employees-row-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employees-row-email {
  font-size: 12px;
  color: var(--lulu-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.employees-row-locked-hint {
  font-size: 11px;
  color: var(--lulu-text-muted);
  font-style: italic;
  white-space: nowrap;
}

.employees-row-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.employees-action-btn {
  background: none;
  border: 1px solid rgba(0, 109, 62, 0.4);
  color: var(--lulu-primary);
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  text-transform: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.12);
}

.employees-action-btn:hover:not(:disabled) {
  background: var(--lulu-primary-soft);
  border-color: var(--lulu-primary);
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
  transform: translateY(-1px);
}

.employees-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.employees-action-btn--danger {
  color: var(--lulu-danger);
  border-color: rgba(220, 38, 38, 0.4);
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.12);
}

.employees-action-btn--danger:hover:not(:disabled) {
  background: var(--lulu-danger-soft);
  border-color: var(--lulu-danger);
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
  transform: translateY(-1px);
}

@media (max-width: 600px) {
  .employees-row {
    grid-template-rows: auto auto;
    gap: 8px 12px;
  }
  .employees-row-actions {
    grid-row: 2;
    grid-column: 1 / span 2;
    justify-content: flex-end;
  }
  /* Touch targets - 44x44 is the WCAG-aligned minimum for interactive
     elements on a touch surface. Bump padding so the row action buttons
     reach that on smaller screens. */
  .employees-action-btn {
    min-height: 44px;
    padding: 10px 14px;
  }
}
</style>
